import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ExternalLinkIcon, RefreshCwIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  awsCredentialsQueries,
  deleteAwsCredentials,
} from '@/lib/queries/awsCredentials';
import { awsRoleQueries, deleteAwsRole } from '@/lib/queries/awsRole';
import { preLoadDefaultProject } from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

import { getRolesColumns, getUsersColumns } from './-columns';

const readOnlyAccessUrl =
  'https://us-east-1.console.aws.amazon.com/iam/home#/policies/details/arn%3Aaws%3Aiam%3A%3Aaws%3Apolicy%2FReadOnlyAccess?section=permissions';

export const Route = createFileRoute('/_root/integrations/aws/')({
  component: RouteComponent,
  loader: async ({ context }) => {
    const defaultProject = await preLoadDefaultProject({ context });
    context.queryClient.prefetchQuery(
      awsCredentialsQueries.list(defaultProject.id),
    );
    context.queryClient.prefetchQuery(awsRoleQueries.list(defaultProject.id));

    return defaultProject;
  },
});

function RouteComponent() {
  const defaultProject = Route.useLoaderData();

  const { data: awsUsers } = useQuery(
    awsCredentialsQueries.list(defaultProject.id),
  );

  const { data: awsRoles } = useQuery(awsRoleQueries.list(defaultProject.id));

  const { mutateAsync: deleteRole } = useMutation({
    mutationFn: (id: string) => deleteAwsRole(defaultProject.id, id),
    mutationKey: generateMutationKey(
      awsRoleQueries.byProject(defaultProject.id),
      'deleteRole',
      true,
    ),
  });

  const { mutateAsync: deleteUser } = useMutation({
    mutationFn: (id: string) => deleteAwsCredentials(defaultProject.id, id),
    mutationKey: generateMutationKey(
      awsCredentialsQueries.byProject(defaultProject.id),
      'deleteUser',
      true,
    ),
  });

  const queryClient = Route.useRouteContext().queryClient;

  const rolesColums = getRolesColumns(deleteRole);
  const usersColums = getUsersColumns(deleteUser);

  return (
    <div className="h-full flex flex-col pb-24">
      <h1 className="text-3xl mb-6">
        <FormattedMessage id="integrations.aws.title" />
      </h1>
      <p>
        <FormattedMessage
          id="integrations.aws.cta"
          values={{
            a: parts => (
              <a
                href={readOnlyAccessUrl}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-blue-500"
              >
                {parts}
                <ExternalLinkIcon className="h-4 inline" />
              </a>
            ),
            br: <br />,
          }}
        />
        <div className="flex flex-row justify-between items-center mt-8">
          <div className="flex flex-row items-baseline">
            <h2 className="text-2xl">
              <FormattedMessage id="integrations.aws.roles.title" />
            </h2>
            <Button
              variant="ghost"
              className="ml-2 h-8 w-8 p-0"
              onClick={() => {
                queryClient.invalidateQueries(
                  awsRoleQueries.list(defaultProject.id),
                );
              }}
            >
              <RefreshCwIcon className="h-4 w-4" />
            </Button>
          </div>
          <Link
            to="/integrations/aws/roles/new"
            className="text-blue-500 hover:text-blue-700"
          >
            <Button variant="main">
              <FormattedMessage id="integrations.aws.roles.new" />
            </Button>
          </Link>
        </div>
        {awsRoles === undefined ? (
          <Skeleton className="bg-background w-full h-8" />
        ) : (
          <DataTable
            data={awsRoles}
            columns={rolesColums}
            headersTranslationPath="integrations.aws.roles.table"
          />
        )}
        <div className="flex flex-row justify-between items-center mt-8">
          <div className="flex flex-row items-baseline">
            <h2 className="text-2xl">
              <FormattedMessage id="integrations.aws.users.title" />
            </h2>
            <Button
              variant="ghost"
              className="ml-2 w-8 h-8 p-0"
              onClick={() => {
                queryClient.invalidateQueries(
                  awsCredentialsQueries.list(defaultProject.id),
                );
              }}
            >
              <RefreshCwIcon className="h-4 w-4" />
            </Button>
          </div>
          <Link
            to="/integrations/aws/users/new"
            className="text-blue-500 hover:text-blue-700"
          >
            <Button variant="main">
              <FormattedMessage id="integrations.aws.users.new" />
            </Button>
          </Link>
        </div>
        {awsUsers === undefined ? (
          <Skeleton className="bg-background w-full h-8" />
        ) : (
          <DataTable
            data={awsUsers}
            columns={usersColums}
            headersTranslationPath="integrations.aws.users.table"
          />
        )}
      </p>
    </div>
  );
}
